import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/apiClient";
import type {
  CourierEarnings, CourierActionResult, DeliveryJob } from "../api/types";
import { Modal } from "../components/Modal";
import { ReasonModal } from "../components/ui/ReasonModal";
import { SUPPORT_EMAIL } from "../config/support";
import { useNotifications } from "../state/NotificationsContext";

/**
 * OD-4 arrival gate (owner decision 2026-07-27): when the arrival tap did not
 * record, this drives the centered warning overlay — either an out-of-radius
 * reading (courier may explicitly override) or no position at all (the floor
 * tablet must confirm instead).
 */
type ArrivalGate =
  | { kind: "far"; distanceM: number; radiusM: number; position?: { lat: number; lng: number } }
  | { kind: "no-gps" };

function mapsUrl(address: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

/**
 * Best-effort one-shot position capture (WP-B). Resolves `undefined` on denial,
 * timeout, or an unsupported browser — the caller always proceeds, so a courier
 * is never blocked from arriving/delivering because location is off.
 */
async function capturePosition(): Promise<{ lat: number; lng: number } | undefined> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return undefined;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(undefined),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  });
}

function slotLabel(job: DeliveryJob): string {
  const day = new Date(job.scheduledDate).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  if (!job.scheduledPickupAt) return day;
  const time = new Date(job.scheduledPickupAt).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} · pickup ${time}`;
}

export function CourierJobsPage() {
  const [jobs, setJobs] = useState<DeliveryJob[]>([]);
  const [history, setHistory] = useState<DeliveryJob[]>([]);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geoWarning, setGeoWarning] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // G-6: "what am I owed?" — delivered jobs with payout, paid/unpaid split.
  const [earnings, setEarnings] = useState<CourierEarnings | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [open, past, availability, earned] = await Promise.allSettled([
        api.courierListJobs(),
        api.courierListHistory(),
        api.courierGetAvailability(),
        api.courierEarnings(),
      ]);
      if (open.status === "fulfilled") setJobs(open.value);
      if (past.status === "fulfilled") setHistory(past.value);
      if (earned.status === "fulfilled") setEarnings(earned.value);
      if (availability.status === "fulfilled") {
        setIsAvailable(availability.value.isAvailable);
      }
      const failed = [open, past, availability].find(
        (result) => result.status === "rejected",
      );
      if (failed?.status === "rejected") {
        setError(
          failed.reason instanceof Error
            ? failed.reason.message
            : "Could not load your deliveries.",
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load your deliveries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A delivery-job notification (assigned / unassigned / cancelled) means the
  // list on screen is stale — refresh it the moment the poll delivers one.
  const { subscribe } = useNotifications();
  useEffect(
    () =>
      subscribe((fresh) => {
        if (fresh.some((n) => n.kind.startsWith("DELIVERY_JOB_"))) {
          void refresh();
        }
      }),
    [subscribe, refresh],
  );

  const toggleAvailability = async () => {
    if (isAvailable === null) return;
    try {
      const result = await api.courierSetAvailability(!isAvailable);
      setIsAvailable(result.isAvailable);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not update availability.");
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">My deliveries</h2>
          <p className="page-subtitle">
            Your assigned runs: pick up at the shop, deliver, confirm with a photo.
            Your location is recorded only when you tap “I’m at the shop” or
            “Delivered”.
          </p>
        </div>
        {isAvailable !== null ? (
          <button
            type="button"
            className={isAvailable ? "button" : "button tinted"}
            onClick={() => void toggleAvailability()}
          >
            {isAvailable ? "Available for jobs" : "Not available"}
          </button>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {geoWarning ? (
        <div className="alert warning" role="status">
          <p style={{ margin: 0 }}>{geoWarning}</p>
          <p style={{ margin: 0 }}>
            If there is any technical issue please contact:{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
          <button
            type="button"
            className="button plain"
            onClick={() => setGeoWarning(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {loading ? <p className="muted">Loading…</p> : null}

      {jobs.length === 0 && !loading ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No deliveries assigned to you right now.
          </p>
        </div>
      ) : (
        jobs.map((job) => (
          <CourierJobCard
            key={job.id}
            job={job}
            onChanged={refresh}
            onError={setError}
            onGeoWarning={setGeoWarning}
          />
        ))
      )}

      {earnings ? (
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <h3 style={{ margin: 0 }}>My earnings, last 30 days</h3>
          <div className="courier-earnings-totals">
            <span>
              <strong>{earnings.totals.unpaidEur} €</strong>{" "}
              <span className="muted">still owed</span>
            </span>
            <span>
              <strong>{earnings.totals.paidEur} €</strong>{" "}
              <span className="muted">already paid</span>
            </span>
            <span>
              <strong>{earnings.totals.jobCount}</strong>{" "}
              <span className="muted">deliveries</span>
            </span>
          </div>
          {earnings.jobs.slice(0, 10).map((job) => (
            <div key={job.id} className="day-shifts-row-head">
              <span className="muted">
                {job.deliveredAt
                  ? new Date(job.deliveredAt).toLocaleDateString()
                  : "—"}{" "}
                · {job.code}
              </span>
              <span>
                {job.payoutEur ?? "—"} €{" "}
                {job.paid ? (
                  <span className="badge success">Paid</span>
                ) : (
                  <span className="badge warning">Owed</span>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <div className="day-shifts-row-head">
          <h3 style={{ margin: 0 }}>Last 30 days</h3>
          <button
            type="button"
            className="button plain"
            onClick={() => setHistoryOpen((cur) => !cur)}
          >
            {historyOpen ? "Hide" : `Show (${history.length})`}
          </button>
        </div>
        {historyOpen
          ? history.map((job) => (
              <div key={job.id} className="day-shifts-row">
                <div className="day-shifts-row-head">
                  <div>
                    <strong>
                      {new Date(job.deliveredAt ?? job.updatedAt).toLocaleDateString()}
                    </strong>{" "}
                    · {job.recipientName}
                  </div>
                  <span
                    className={job.status === "DELIVERED" ? "badge success" : "badge danger"}
                  >
                    {job.status === "DELIVERED" ? "delivered" : "failed"}
                  </span>
                </div>
                <p className="muted" style={{ margin: 0 }}>
                  {job.deliveryAddress}
                  {job.courierPayoutEur ? ` · ${job.courierPayoutEur} €` : ""}
                  {job.status === "DELIVERED"
                    ? job.paidToCourier
                      ? " · paid"
                      : " · payout pending"
                    : ""}
                </p>
              </div>
            ))
          : null}
        {historyOpen && history.length === 0 ? (
          <p className="muted">Nothing in the last 30 days.</p>
        ) : null}
      </div>
    </section>
  );
}

function CourierJobCard({
  job,
  onChanged,
  onError,
  onGeoWarning,
}: {
  job: DeliveryJob;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
  onGeoWarning: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [arrivalGate, setArrivalGate] = useState<ArrivalGate | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const run = async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    onError(null);
    try {
      const result = await action();
      await onChanged();
      return result;
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not update the delivery.");
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Advisory warning only — the action has already been recorded server-side, so
   * the copy must say so or the courier will assume the tap failed and retry.
   */
  const reportGeoCheck = (result: CourierActionResult | undefined, place: string) => {
    if (!result?.geoCheck || result.geoCheck.withinRadius) return;
    onGeoWarning(
      `Geolocation detects that you are not ${place} — it places you about ` +
        `${result.geoCheck.distanceM} m away. This has still been recorded.`,
    );
  };

  const deliverWith = async (photo: File | null) => {
    onGeoWarning(null);
    const position = await capturePosition();
    const form = new FormData();
    if (photo) form.append("photo", photo);
    if (position) {
      form.append("lat", String(position.lat));
      form.append("lng", String(position.lng));
    }
    const result = await run(() => api.courierDeliverJob(job.id, form));
    reportGeoCheck(result, "at the delivery address");
  };

  /**
   * OD-4 arrival gate: inside 50 m records immediately; outside shows the
   * centered warning with the flagged "I am at the shop anyway" override; no
   * position at all routes the courier to the florist's tablet confirmation.
   */
  const arrivedAtShop = async (
    override = false,
    priorPosition?: { lat: number; lng: number },
  ) => {
    onGeoWarning(null);
    setArrivalGate(null);
    const position = override ? priorPosition : await capturePosition();
    const result = await run(() => api.courierArrivedJob(job.id, position, override));
    if (!result || result.arrivalRecorded !== false) return;
    if (result.arrivalBlocked === "OUT_OF_RADIUS" && result.geoCheck) {
      setArrivalGate({
        kind: "far",
        distanceM: result.geoCheck.distanceM,
        radiusM: result.geoCheck.radiusM,
        position,
      });
    } else if (result.arrivalBlocked === "NO_POSITION") {
      setArrivalGate({ kind: "no-gps" });
    }
  };

  // D-11: kit ReasonModal instead of native browser dialogs — iOS "block
  // dialogs" can suppress native dialogs, which would strand the courier.
  const [confirmAction, setConfirmAction] = useState<
    "fail" | "no-photo" | "problem" | null
  >(null);
  const [problemSent, setProblemSent] = useState(false);

  const fail = async (reason: string | undefined) => {
    if (!reason || reason.length < 3) {
      onError("A short reason is required to mark a delivery as failed.");
      return;
    }
    setConfirmAction(null);
    await run(() => api.courierFailJob(job.id, reason));
  };

  // G-4: a flare, not a state — position captured only at the send tap, and
  // denied GPS still sends the report (coords omitted).
  const reportProblem = async (message: string | undefined) => {
    if (!message || message.length < 3) {
      onError("Describe the problem in a few words.");
      return;
    }
    setConfirmAction(null);
    const position = await capturePosition();
    const result = await run(() =>
      api.courierReportProblem(job.id, message, position),
    );
    if (result) setProblemSent(true);
  };

  const assigned = job.status === "ASSIGNED";

  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <div className="day-shifts-row-head">
        <strong>{slotLabel(job)}</strong>
        <span className={assigned ? "badge primary" : "badge warning"}>
          {assigned ? "to pick up" : "on the way"}
        </span>
      </div>

      {job.order ? (
        <div>
          <p className="muted" style={{ margin: 0 }}>
            Carrying · {job.order.code}
          </p>
          {job.order.lines.map((line) => (
            <p key={line.id} style={{ margin: 0 }}>
              {Number(line.quantity) === 1
                ? line.description
                : `${line.quantity} × ${line.description}`}
            </p>
          ))}
        </div>
      ) : null}

      <div>
        <p className="muted" style={{ margin: 0 }}>
          Pickup · {job.location.name}
        </p>
        <p style={{ margin: 0 }}>{job.pickupAddress}</p>
      </div>

      <div>
        <p className="muted" style={{ margin: 0 }}>
          Deliver to · {job.recipientName}
        </p>
        <p style={{ margin: 0 }}>
          <a href={mapsUrl(job.deliveryAddress)} target="_blank" rel="noopener noreferrer">
            {job.deliveryAddress}
          </a>
        </p>
        {job.recipientPhone ? (
          <p style={{ margin: 0 }}>
            <a href={`tel:${job.recipientPhone.replace(/\s+/g, "")}`}>
              {job.recipientPhone}
            </a>
          </p>
        ) : null}
      </div>

      {job.recipientNotes ? <p style={{ margin: 0 }}>{job.recipientNotes}</p> : null}

      <p className="muted" style={{ margin: 0 }}>
        {job.distanceKm ? `${job.distanceKm} km · ` : ""}
        {job.courierPayoutEur ? `your payout ${job.courierPayoutEur} €` : ""}
        {/* OD-30: couriers never take money — the shop settles every order
            before pickup (OD-101), so there is nothing to collect here. */}
      </p>

      {assigned &&
      (job.acceptedAt || job.arrivedAtPickupAt || job.handoverConfirmedAt) ? (
        <p className="muted" style={{ margin: 0 }}>
          {job.acceptedAt ? "Accepted" : null}
          {job.acceptedAt && (job.arrivedAtPickupAt || job.handoverConfirmedAt)
            ? " · "
            : null}
          {job.arrivedAtPickupAt
            ? "At the shop"
            : job.handoverConfirmedAt
              ? "Handover confirmed by the shop"
              : null}
        </p>
      ) : null}

      <div className="button-row">
        {assigned ? (
          /* OD-4: one primary action at a time — Accept → I'm at the shop →
             Picked up. Route stays a secondary link. The shop's handover
             confirmation unlocks Picked up for a courier whose phone gave no
             position (OD-28, the KI-4 fix). */
          <>
            <a
              href={mapsUrl(job.pickupAddress)}
              target="_blank"
              rel="noopener noreferrer"
              className="button plain"
            >
              Route to shop
            </a>
            {!job.acceptedAt ? (
              <button
                type="button"
                className="button"
                onClick={() =>
                  void run(async () =>
                    api.courierAcceptJob(job.id, await capturePosition()),
                  )
                }
                disabled={busy}
              >
                {busy ? "Saving…" : "Accept"}
              </button>
            ) : !job.arrivedAtPickupAt && !job.handoverConfirmedAt ? (
              <button
                type="button"
                className="button"
                onClick={() => void arrivedAtShop()}
                disabled={busy}
              >
                {busy ? "Saving…" : "I'm at the shop"}
              </button>
            ) : job.order?.webAwaitsHandover ? (
              /* G-166: the shop hands a web order over on its web orders list
                 first; the server refuses the tap until then. */
              <span className="muted">Waiting for the shop to hand this web order over</span>
            ) : (
              <button
                type="button"
                className="button"
                onClick={() => void run(() => api.courierPickupJob(job.id))}
                disabled={busy}
              >
                {busy ? "Saving…" : "Picked up"}
              </button>
            )}
            <button
              type="button"
              className="button plain"
              onClick={() => setConfirmAction("problem")}
              disabled={busy}
            >
              Report a problem
            </button>
          </>
        ) : (
          <>
            <a
              href={mapsUrl(job.deliveryAddress)}
              target="_blank"
              rel="noopener noreferrer"
              className="button plain"
            >
              Route to client
            </a>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                e.target.value = "";
                void deliverWith(file);
              }}
            />
            <button
              type="button"
              className="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={busy}
            >
              {busy ? "Saving…" : "Delivered — take photo"}
            </button>
            <button
              type="button"
              className="button plain"
              onClick={() => setConfirmAction("no-photo")}
              disabled={busy}
            >
              No photo
            </button>
            <button
              type="button"
              className="button plain"
              onClick={() => setConfirmAction("problem")}
              disabled={busy}
            >
              Report a problem
            </button>
            <button
              type="button"
              className="button danger"
              onClick={() => setConfirmAction("fail")}
              disabled={busy}
            >
              Cannot deliver
            </button>
          </>
        )}
      </div>

      {problemSent ? (
        <p className="muted" style={{ margin: 0 }}>
          Problem reported — the shop has been notified. The delivery continues
          as normal.
        </p>
      ) : null}

      {confirmAction === "fail" ? (
        <ReasonModal
          title="Cannot deliver?"
          message="This marks the delivery as failed and alerts the shop."
          reasonMode="required"
          reasonLabel="Why can't this be delivered?"
          minReasonLength={3}
          confirmLabel="Mark as failed"
          destructive
          busy={busy}
          onConfirm={(reason) => void fail(reason)}
          onClose={() => setConfirmAction(null)}
        />
      ) : null}

      {confirmAction === "problem" ? (
        <ReasonModal
          title="Report a problem"
          message="The shop and the owner are notified right away; the delivery continues as normal."
          reasonMode="required"
          reasonLabel="What is wrong?"
          reasonPlaceholder="e.g. client not answering the door"
          minReasonLength={3}
          confirmLabel="Send report"
          busy={busy}
          onConfirm={(message) => void reportProblem(message)}
          onClose={() => setConfirmAction(null)}
        />
      ) : null}

      {confirmAction === "no-photo" ? (
        <ReasonModal
          title="Deliver without a photo?"
          message="The delivery will be recorded without proof. You can still take the photo instead."
          confirmLabel="Deliver without photo"
          busy={busy}
          onConfirm={() => {
            setConfirmAction(null);
            void deliverWith(null);
          }}
          onClose={() => setConfirmAction(null)}
        />
      ) : null}

      {arrivalGate ? (
        <Modal
          title={arrivalGate.kind === "far" ? "Not at the shop?" : "No location"}
          onClose={() => setArrivalGate(null)}
        >
          {arrivalGate.kind === "far" ? (
            <>
              <p>
                Geolocation places you about {arrivalGate.distanceM} m from the
                shop (limit {arrivalGate.radiusM} m). Your arrival has NOT been
                recorded.
              </p>
              <div className="button-row">
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => {
                    const gate = arrivalGate;
                    void arrivedAtShop(true, gate.position);
                  }}
                >
                  I am at the shop anyway
                </button>
                <button
                  type="button"
                  className="button plain"
                  onClick={() => setArrivalGate(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p>
                We could not read your location, so your arrival has NOT been
                recorded. Ask the florist to tap “Picked up by courier” on the
                shop tablet — the shop’s confirmation stands in for your
                position — then refresh this list.
              </p>
              <div className="button-row">
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setArrivalGate(null);
                    void onChanged();
                  }}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="button plain"
                  onClick={() => setArrivalGate(null)}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
    </div>
  );
}
